import ButtonBasic from "@rentiohq/shared-frontend/dist/components/components/ButtonBasic";
import FullScreenActivityIndicator from "@rentiohq/shared-frontend/dist/components/components/FullScreenActivityIndicator";
import { FullScreenErrorState } from "@rentiohq/shared-frontend/dist/components/components/FullScreenState";
import ListItemSeparator from "@rentiohq/shared-frontend/dist/components/components/ListItemSeparator";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as paymentRequestActions from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.actions";
import * as paymentRequestSelectors from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.selectors";
import * as paymentRequestUtils from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.utils";
import { useDetail } from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import {
  EPaymentRequestFetchType,
  EPaymentRequestStatus,
  IPaymentRequest,
} from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { getTitle } from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import React from "react";
import { isAndroid, isIOS } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { IRootState } from "redux/reducers";
import InfoHeader from "../../../../components/InfoHeader";
import Page from "../../../../components/Page";
import withAuthenticationCheck from "../../../../hoc/withAuthenticationCheck";
import * as S from "./PaymentRequestListItem.styles";

const APP_STORE_URL = "https://apps.apple.com/be/app/rentio/id1435785897?l=nl";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=be.rentio.app&hl=nl";
const WEB_APP_URL = "https://rentio.be/nl-be/app";

interface IPaymentRequestListItemProps {
  paymentRequest: IPaymentRequest;
}

const PaymentRequestListItem = (props: IPaymentRequestListItemProps) => {
  const { paymentRequest } = props;

  const propertyId = paymentRequest.requestPropertyId;

  const { detail: property } = useDetail({
    shouldRefetch: false,
    id: propertyId,
  });

  const navigate = useNavigate();

  const handlePressPay = () => {
    navigate(`/payment/${paymentRequest.id}`);
  };

  // Render
  const infoItems = [];

  if (paymentRequest.status !== EPaymentRequestStatus.New) {
    infoItems.push(
      getLocalizedText(
        `payment.payment_request.status.${paymentRequest.status}`.toLowerCase(),
      ),
    );
  }

  infoItems.push(
    getLocalizedText("payment_request.info.pay_before", {
      date: formatDate(paymentRequest.dueDateAt),
    }),
  );

  return (
    <S.Wrap onClick={handlePressPay}>
      <S.LeftWrap>
        <S.Title>
          {getTitle(paymentRequest)}
          {" - "}
          {formatCurrency(paymentRequest.amount)}
        </S.Title>

        {property && (
          <S.Address>
            {property.propertyAddress?.full || formatAddress(property)}
          </S.Address>
        )}

        <S.Info>{infoItems.join(" | ")}</S.Info>
      </S.LeftWrap>

      <ButtonBasic
        title={`${getLocalizedText("system.next")} ›`}
        onPress={handlePressPay}
      />
    </S.Wrap>
  );
};

const HomeBase = () => {
  const dispatch = useDispatch();

  const { user, contactMe } = authHooks.useSelf();

  const isFetchingOpenPaymentRequests = useSelector(
    (state: IRootState) =>
      paymentRequestSelectors.getPaged(state, {
        page: 1,
        limit: CONFIG.DEFAULT_FETCH_LIMIT,
        filter: {
          requestPayerAccountId: { in: contactMe?.accountIds },
          status: {
            in: paymentRequestUtils.getPaymentRequestTypes(
              EPaymentRequestFetchType.Open,
            ),
          },
        },
      })?.isFetching,
  );
  const fetchOpenPaymentRequestError = useSelector(
    (state: IRootState) =>
      paymentRequestSelectors.getPaged(state, {
        page: 1,
        limit: CONFIG.DEFAULT_FETCH_LIMIT,
        filter: {
          requestPayerAccountId: { in: contactMe?.accountIds },
          status: {
            in: paymentRequestUtils.getPaymentRequestTypes(
              EPaymentRequestFetchType.Open,
            ),
          },
        },
      })?.fetchError,
  );
  const openPaymentRequests = useSelector(
    (state: IRootState) =>
      paymentRequestSelectors.getPaged(state, {
        page: 1,
        limit: CONFIG.DEFAULT_FETCH_LIMIT,
        filter: {
          requestPayerAccountId: { in: contactMe?.accountIds },
          status: {
            in: paymentRequestUtils.getPaymentRequestTypes(
              EPaymentRequestFetchType.Open,
            ),
          },
        },
      })?.items,
  );

  // Data
  const fetchData = () => {
    if (!contactMe) {
      return;
    }

    dispatch(
      paymentRequestActions.getPagedStart.getAction({
        query: {
          page: 1,
          limit: CONFIG.DEFAULT_FETCH_LIMIT,
          filter: {
            requestPayerAccountId: { in: contactMe?.accountIds },
            status: {
              in: paymentRequestUtils.getPaymentRequestTypes(
                EPaymentRequestFetchType.Open,
              ),
            },
          },
        },
      }),
    );
  };

  // Lifecycle
  React.useEffect(() => {
    fetchData();
  }, [contactMe?.accountIds]);

  // Event handlers
  const handlePressOpenApp = async () => {
    let url = WEB_APP_URL;
    if (isIOS) {
      url = APP_STORE_URL;
    }
    if (isAndroid) {
      url = PLAY_STORE_URL;
    }
    window.open(url, "_blank");
  };

  // Helpers
  const getOpenPaymentRequestsWhereIAmPayer = () => {
    if (!user) {
      return [];
    }

    return (
      openPaymentRequests?.filter(({ payerAccount }) => {
        if (!payerAccount) {
          return false;
        }

        if (payerAccount.isSelf) {
          return true;
        }

        if (user.activeAccountId === payerAccount.id) {
          return true;
        }

        if (payerAccount.userId && payerAccount.userId === user.id) {
          return true;
        }

        return false;
      }) || []
    );
  };

  // Render
  if (isFetchingOpenPaymentRequests || !contactMe) {
    return (
      <Page withScroll={false}>
        <FullScreenActivityIndicator />
      </Page>
    );
  }

  if (fetchOpenPaymentRequestError) {
    return (
      <Page withScroll={false}>
        <FullScreenErrorState
          showIcon={false}
          error={fetchOpenPaymentRequestError}
          title={getLocalizedText("general.error")}
          actionTitle={getLocalizedText("try.again")}
          onPressAction={fetchData}
        />
      </Page>
    );
  }

  const openPaymentRequestsWhereIAmPayer =
    getOpenPaymentRequestsWhereIAmPayer();

  if (openPaymentRequestsWhereIAmPayer.length === 0) {
    return (
      <Page>
        <InfoHeader
          title={getLocalizedText("pay.empty.title")}
          body={getLocalizedText("pay.empty.body")}
          largeButton={true}
          buttonTitle={getLocalizedText("pay.empty.cta.download_app")}
          onPressButton={handlePressOpenApp}
        />
      </Page>
    );
  }

  if (openPaymentRequestsWhereIAmPayer.length > 1) {
    return (
      <Page>
        <InfoHeader
          title={getLocalizedText("pay.multiple.title", {
            count: `${openPaymentRequestsWhereIAmPayer.length}`,
          })}
          body={getLocalizedText("pay.multiple.body", {
            count: `${openPaymentRequestsWhereIAmPayer.length}`,
          })}
        />
        <Spacer weight={ESpacerWeight.W24} />

        <S.PaymentRequestsWrap>
          {openPaymentRequestsWhereIAmPayer.map((paymentRequest, index) => (
            <>
              {index > 0 && <ListItemSeparator />}
              <PaymentRequestListItem paymentRequest={paymentRequest} />
            </>
          ))}
        </S.PaymentRequestsWrap>
      </Page>
    );
  }

  return <Navigate to={`/payment/${openPaymentRequestsWhereIAmPayer[0].id}`} />;
};

export const Home = withAuthenticationCheck(HomeBase);
